type EndpointKey =
  | 'getUserSubscription'
  | 'createCheckoutSession'
  | 'createPortalSession'
  | 'createPortalSessionByCustomerId';

const backendBaseUrl = import.meta.env.VITE_BACKEND_BASE_URL?.replace(/\/$/, '');

const endpointPaths: Record<EndpointKey, string> = {
  getUserSubscription: '/get-user-subscription',
  createCheckoutSession: '/create-checkout-session',
  createPortalSession: '/create-portal-session',
  createPortalSessionByCustomerId: '/create-portal-session-by-customer-id'
};

const endpointEnvOverrides: Partial<Record<EndpointKey, string | undefined>> = {
  getUserSubscription: import.meta.env.VITE_GET_USER_SUBSCRIPTION_URL,
  createCheckoutSession: import.meta.env.VITE_CREATE_CHECKOUT_SESSION_URL,
  createPortalSession: import.meta.env.VITE_CREATE_PORTAL_SESSION_URL,
  createPortalSessionByCustomerId: import.meta.env.VITE_CREATE_PORTAL_SESSION_BY_CUSTOMER_URL
};

const fallbackEndpoints: Record<EndpointKey, string> = {
  getUserSubscription: 'https://get-user-subscription-4lwrmzn6aa-uc.a.run.app',
  createCheckoutSession: 'https://create-checkout-session-4lwrmzn6aa-uc.a.run.app',
  createPortalSession: 'https://create-portal-session-4lwrmzn6aa-uc.a.run.app',
  createPortalSessionByCustomerId: 'https://create-portal-session-by-customer-id-4lwrmzn6aa-uc.a.run.app'
};

const buildEndpointUrl = (key: EndpointKey) => {
  const override = endpointEnvOverrides[key];
  if (override) {
    return override;
  }

  if (backendBaseUrl) {
    const path = endpointPaths[key];
    return `${backendBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  return fallbackEndpoints[key];
};

const appendQueryParams = (url: string, params?: Record<string, string | number | undefined>) => {
  if (!params) {
    return url;
  }

  const searchParams = new URLSearchParams();

  let hasParams = false;

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
      hasParams = true;
    }
  });

  if (!hasParams) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${searchParams.toString()}`;
};

const handleJsonResponse = async <T>(response: Response) => {
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with status ${response.status}`);
  }

  return response.json() as Promise<T>;
};

export interface SubscriptionResponse {
  exists: boolean;
  subscription: any;
}

export const fetchUserSubscription = async (userId: string) => {
  const url = appendQueryParams(buildEndpointUrl('getUserSubscription'), { user_id: userId });
  return handleJsonResponse<SubscriptionResponse>(await fetch(url));
};

export interface CheckoutSessionRequest {
  lookup_key: string;
  success_url: string;
  cancel_url: string;
  user_id: string;
}

export const requestCheckoutSession = async (payload: CheckoutSessionRequest) => {
  const url = buildEndpointUrl('createCheckoutSession');
  return handleJsonResponse<{ url: string }>(
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
  );
};

export interface PortalSessionPayload {
  session_id?: string;
  customer_id?: string;
  return_url: string;
}

export const requestPortalSession = async (payload: PortalSessionPayload) => {
  const url = buildEndpointUrl(payload.customer_id ? 'createPortalSessionByCustomerId' : 'createPortalSession');
  return handleJsonResponse<{ url: string }>(
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
  );
};
